import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import 'leaflet-routing-machine';
import '../styles/components/ItineraryMap.css';
import POIDetails from './POIDetails';
import CustomActivityDetailsModal from './CustomActivityDetailsModal';

const getMarkerColor = (type) => {
  switch (type) {
    case 'restaurant':
      return '#F2C143';
    case 'hotel':
      return '#808080';
    case 'exploration':
      return '#57B2A6';
    case 'task':
      return '#FFA07A';
    case 'rent_a_car':
      return '#FF6347';
    case 'poi':
      return '#4CAF50';
    default:
      return '#87CEEB'; // Changed default to exploration color
  }
};

const CustomOverlay = ({ poi, index, onPoiClick }) => {
  const map = useMap();
  const markerRef = useRef(null);

  useEffect(() => {
    if (!map || !poi.lat || !poi.lng) return;

    const element = document.createElement('div');
    element.className = 'custom-overlay';
    
    const markerColor = getMarkerColor(poi.type);
    
    let imageUrl = poi.image_url;
    if (typeof imageUrl === 'string' && imageUrl.startsWith('[')) {
      const parsedImages = JSON.parse(imageUrl);
      imageUrl = parsedImages[0];
    }
    
    element.innerHTML = `
      <div class="marker-background" style="background-color: ${markerColor};">
        <div class="marker-number">${index + 1}</div>
      </div>
      <div class="marker-image" style="background-image: url(${imageUrl});"></div>
    `;

    const marker = L.marker([poi.lat, poi.lng], {
      icon: L.divIcon({
        className: 'custom-icon',
        html: element,
        iconSize: [44, 44],
        iconAnchor: [22, 44],
      })
    }).addTo(map);

    markerRef.current = marker;

    element.addEventListener('mouseover', () => element.classList.add('hovered'));
    element.addEventListener('mouseout', () => element.classList.remove('hovered'));
    element.addEventListener('click', () => onPoiClick(poi));

    return () => {
      if (map && marker) {
        map.removeLayer(marker);
      }
    };
  }, [map, poi, index, onPoiClick]);

  return null;
};

const MapBounds = () => {
  const map = useMap();
  const drBounds = L.latLngBounds(
    L.latLng(17.5, -72), // Southwest corner
    L.latLng(19.9, -68)  // Northeast corner
  );

  useEffect(() => {
    map.setMaxBounds(drBounds);
    map.on('drag', () => {
      map.panInsideBounds(drBounds, { animate: false });
    });
  }, [map, drBounds]);

  return null;
};

const FitBoundsComponent = ({ pois }) => {
  const map = useMap();

  useEffect(() => {
    if (!pois || pois.length === 0) return;

    // Create bounds object from POI coordinates
    const bounds = L.latLngBounds(pois.map(poi => [poi.lat, poi.lng]));

    // If we only have one POI, create a slightly larger bounds
    if (pois.length === 1) {
      const center = bounds.getCenter();
      bounds.extend([center.lat + 0.1, center.lng + 0.1]);
      bounds.extend([center.lat - 0.1, center.lng - 0.1]);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    } else {
      // Calculate the average distance between all POIs
      let totalDistance = 0;
      let pairCount = 0;
      
      for (let i = 0; i < pois.length; i++) {
        for (let j = i + 1; j < pois.length; j++) {
          const point1 = L.latLng(pois[i].lat, pois[i].lng);
          const point2 = L.latLng(pois[j].lat, pois[j].lng);
          totalDistance += point1.distanceTo(point2);
          pairCount++;
        }
      }
      
      const averageDistance = pairCount > 0 ? totalDistance / pairCount : 0;
      
      // Adjust threshold based on average distance
      const isGrouped = averageDistance < 15000; // 15km threshold
      
      const options = {
        padding: isGrouped ? [150, 150] : [50, 50],
        maxZoom: isGrouped ? 16 : 11,
        // Add a minimum zoom to prevent too much zoom out
        minZoom: 9
      };
      
      map.fitBounds(bounds, options);
    }
  }, [map, pois]);

  return null;
};

const ItineraryMap = ({ pois, amenities, onUpdateCustomActivity, isReadOnly }) => {
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [selectedCustomActivity, setSelectedCustomActivity] = useState(null);
  const mapRef = useRef(null);

  const validPOIs = useMemo(() => {
    return pois.filter(poi => poi.lat && poi.lng).sort((a, b) => {
      if (a.day !== b.day) return a.day - b.day;
      if (a.hour !== b.hour) return a.hour - b.hour;
      return a.order - b.order;
    });
  }, [pois]);

  const drCenter = [18.7357, -70.1627];
  const defaultZoom = 8;

  const handlePoiClick = (poi) => {
    if (poi.isCustom) {
      setSelectedCustomActivity(poi);
    } else {
      setSelectedPOI(poi);
    }
  };

  const handleCloseModal = () => {
    setSelectedPOI(null);
    setSelectedCustomActivity(null);
  };

  const handleUpdateCustomActivity = async (updatedActivity) => {
    try {
      await onUpdateCustomActivity(updatedActivity);
      setSelectedCustomActivity(null);
    } catch (error) {
      console.error('Error updating custom activity:', error);
    }
  };

  const memoizedMap = useMemo(() => (
    <MapContainer 
      center={drCenter}
      zoom={defaultZoom}
      className="itinerary-map"
      minZoom={7}
      whenCreated={(map) => { mapRef.current = map; }}
      zoomControl={false}
    >
      <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
      <MapBounds />
      <FitBoundsComponent pois={validPOIs} />
      {validPOIs.map((poi, index) => (
        <CustomOverlay 
          key={`${poi.id}-${poi.day}-${poi.hour}`}
          poi={poi}
          index={index}
          onPoiClick={handlePoiClick}
        />
      ))}
    </MapContainer>
  ), [validPOIs]);

  return (
    <>
      {memoizedMap}
      {selectedPOI && !selectedPOI.isCustom && (
        <POIDetails 
          poi={selectedPOI} 
          onClose={handleCloseModal} 
          amenities={amenities}
          isModal={true}
        />
      )}
      {selectedCustomActivity && (
        <CustomActivityDetailsModal
          activity={selectedCustomActivity}
          onClose={handleCloseModal}
          onUpdate={handleUpdateCustomActivity}
          isReadOnly={isReadOnly}
        />
      )}
    </>
  );
};

export default React.memo(ItineraryMap);
